class EconoSmartNavigation {
  constructor() {
    this.body = document.body;
    this.header = document.querySelector('[data-site-header]');
    this.announcement = document.querySelector('.announcement-bar');
    this.mobileMenu = document.querySelector('[data-mobile-menu]');
    this.mobileOpen = document.querySelector('[data-mobile-menu-open]');
    this.mobileClose = document.querySelectorAll('[data-mobile-menu-close]');
    this.floatingNavigation = document.querySelector('[data-floating-navigation]');
    this.menuItems = Array.from(document.querySelectorAll('[data-header-menu-item]'));
    this.menuBackdrop = document.querySelector('[data-header-menu-backdrop]');
    this.lastScrollY = window.scrollY;
    this.scrollTicking = false;
    this.activeMenuItem = null;
    this.lastMobileTrigger = null;
    this.init();
  }

  init() {
    this.syncAnnouncementHeight();
    this.initMobileNavigation();
    this.initDesktopMenus();
    this.initHeaderScroll();
    this.initFloatingNavigation();
    this.initKeyboardEvents();

    window.addEventListener('resize', () => this.syncAnnouncementHeight(), { passive: true });
  }

  syncAnnouncementHeight() {
    const height = this.announcement?.offsetHeight || 0;
    document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
  }

  initMobileNavigation() {
    if (!this.mobileMenu || !this.mobileOpen) return;

    this.mobileOpen.addEventListener('click', () => this.openMobileMenu());
    this.mobileClose.forEach((element) => {
      element.addEventListener('click', () => this.closeMobileMenu());
    });
  }

  openMobileMenu() {
    this.closeDesktopMenu();
    this.lastMobileTrigger = document.activeElement;
    this.mobileMenu.classList.add('is-open');
    this.mobileMenu.setAttribute('aria-hidden', 'false');
    this.mobileOpen.setAttribute('aria-expanded', 'true');
    this.body.classList.add('mobile-menu-open');
    window.setTimeout(() => this.mobileMenu.querySelector('[data-mobile-menu-close]')?.focus(), 60);
  }

  closeMobileMenu() {
    if (!this.mobileMenu) return;

    const wasOpen = this.mobileMenu.classList.contains('is-open');

    this.mobileMenu.classList.remove('is-open');
    this.mobileMenu.setAttribute('aria-hidden', 'true');
    if (this.mobileOpen) this.mobileOpen.setAttribute('aria-expanded', 'false');
    this.body.classList.remove('mobile-menu-open');

    if (wasOpen && this.lastMobileTrigger?.isConnected) {
      this.lastMobileTrigger.focus();
    }
  }

  initDesktopMenus() {
    if (!this.header || !this.menuItems.length) return;

    this.menuItems.forEach((item) => {
      const trigger = item.querySelector('[data-header-menu-trigger]');
      if (!trigger) return;

      trigger.setAttribute('aria-expanded', 'false');

      item.addEventListener('mouseenter', () => {
        if (window.matchMedia('(max-width: 989px)').matches) return;
        this.openDesktopMenu(item);
      });

      item.addEventListener('mouseleave', () => {
        if (window.matchMedia('(max-width: 989px)').matches) return;
        this.closeDesktopMenu(item);
      });

      item.addEventListener('focusin', () => {
        if (window.matchMedia('(max-width: 989px)').matches) return;
        this.openDesktopMenu(item);
      });

      item.addEventListener('focusout', (event) => {
        if (item.contains(event.relatedTarget)) return;
        this.closeDesktopMenu(item);
      });
    });

    this.menuBackdrop?.addEventListener('click', () => this.closeDesktopMenu());
  }

  openDesktopMenu(item) {
    if (!this.header || !item) return;

    if (this.activeMenuItem && this.activeMenuItem !== item) {
      this.activeMenuItem.querySelector('[data-header-menu-trigger]')?.setAttribute('aria-expanded', 'false');
    }

    this.activeMenuItem = item;
    item.querySelector('[data-header-menu-trigger]')?.setAttribute('aria-expanded', 'true');
    this.header.classList.add('is-menu-open');

    if (this.header.dataset.megaBackdrop === 'true') {
      this.menuBackdrop?.classList.add('is-visible');
    }
  }

  closeDesktopMenu(item = null) {
    if (item && this.activeMenuItem && item !== this.activeMenuItem) return;

    this.menuItems.forEach((menuItem) => {
      menuItem.querySelector('[data-header-menu-trigger]')?.setAttribute('aria-expanded', 'false');
    });

    this.activeMenuItem = null;
    this.header?.classList.remove('is-menu-open');
    this.menuBackdrop?.classList.remove('is-visible');
  }

  initKeyboardEvents() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeDesktopMenu();
        this.closeMobileMenu();
        return;
      }

      if (event.key === 'Tab' && this.mobileMenu?.classList.contains('is-open')) {
        this.trapMobileFocus(event);
      }
    });
  }

  trapMobileFocus(event) {
    const drawer = this.mobileMenu?.querySelector('.mobile-navigation__drawer');
    if (!drawer) return;

    const focusable = Array.from(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  initHeaderScroll() {
    if (!this.header) return;

    const behavior = this.header.dataset.headerBehavior || 'none';
    const solidThreshold = Number(this.header.dataset.solidThreshold) || 0;

    if (behavior !== 'none') {
      this.header.classList.add('site-header--sticky');
    }

    const update = () => {
      const currentY = window.scrollY;
      const delta = currentY - this.lastScrollY;
      const isScrolled = currentY > 0;
      const isSolid = currentY > solidThreshold;
      const drawersOpen =
        this.body.classList.contains('drawer-open') ||
        this.body.classList.contains('mobile-menu-open') ||
        this.header.classList.contains('is-menu-open');

      this.header.classList.toggle('is-scrolled', isScrolled);
      this.header.classList.toggle('is-solid', isSolid);

      if (behavior === 'reveal') {
        if (currentY <= Math.max(solidThreshold, 24) || drawersOpen) {
          this.header.classList.remove('is-hidden');
        } else if (delta > 7 && currentY > this.header.offsetHeight + 80) {
          this.header.classList.add('is-hidden');
          this.closeDesktopMenu();
        } else if (delta < -7) {
          this.header.classList.remove('is-hidden');
        }
      }

      this.lastScrollY = currentY;
      this.scrollTicking = false;
    };

    window.addEventListener('scroll', () => {
      if (this.scrollTicking) return;
      window.requestAnimationFrame(update);
      this.scrollTicking = true;
    }, { passive: true });

    update();
  }

  initFloatingNavigation() {
    if (!this.floatingNavigation) return;

    const threshold = Number(this.floatingNavigation.dataset.threshold) || 250;
    let ticking = false;

    const updateNavigation = () => {
      this.floatingNavigation.classList.toggle('is-visible', window.scrollY >= threshold);
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (ticking) return;
      window.requestAnimationFrame(updateNavigation);
      ticking = true;
    }, { passive: true });

    updateNavigation();

    /* =========================================================
   Hide floating navigation when footer enters viewport
   ========================================================= */
  const footer =
    document.querySelector('.site-footer');

    if (footer) {

      const footerObserver =
        new IntersectionObserver(
          (entries) => {

            entries.forEach((entry) => {

              this.floatingNavigation.classList.toggle(
                'is-footer-hidden',
                entry.isIntersecting
              );

            });

          },
          {
            threshold: 0.01
          }
        );

      footerObserver.observe(footer);

    }
  }
}

function initEconoSmartNavigation() {
  if (window.econoSmartNavigation) return;
  window.econoSmartNavigation = new EconoSmartNavigation();
}

document.addEventListener('DOMContentLoaded', initEconoSmartNavigation);

document.addEventListener('shopify:section:load', () => {
  window.econoSmartNavigation?.syncAnnouncementHeight();
});